import type { CategoryDef, ScoreContext, VariantDef } from '../types';
import {
  containsAll,
  counts,
  highestOfAKind,
  isFullHouse,
  pairValues,
  sum,
} from '../dice';
import {
  UPPER_IDS,
  chance,
  fixedCategory,
  noBonus,
  ofAKindAll,
  ofAKindOnly,
  straightExact,
  thresholdBonus,
  upperCategories,
} from './shared';

const base: Pick<
  VariantDef,
  | 'columns'
  | 'upperCategoryIds'
  | 'fiveKind'
  | 'sharedRolls'
  | 'sequential'
  | 'streak'
  | 'powers'
  | 'letterFaces'
  | 'pipColors'
  | 'servedInstantWin'
> = {
  columns: [1],
  upperCategoryIds: UPPER_IDS,
  fiveKind: null,
  sharedRolls: false,
  sequential: false,
  streak: null,
  powers: null,
  letterFaces: null,
  pipColors: null,
  servedInstantWin: null,
};

/* ---------- Yatzy (Scandinavia) ---------- */

const onePair: CategoryDef = {
  id: 'onePair',
  name: 'One Pair',
  short: '1P',
  section: 'lower',
  hint: 'Highest pair: sum of those two dice',
  score: ctx => highestOfAKind(ctx.dice, 2) * 2,
};

const twoPairs: CategoryDef = {
  id: 'twoPairs',
  name: 'Two Pairs',
  short: '2P',
  section: 'lower',
  hint: 'Two different pairs: sum of those four dice',
  score: ctx => {
    const p = pairValues(ctx.dice);
    return p.length >= 2 ? (p[0]! + p[1]!) * 2 : 0;
  },
};

const yatzyFullHouse: CategoryDef = {
  id: 'fullHouse',
  name: 'Full House',
  short: 'FH',
  section: 'lower',
  hint: '3 of one and 2 of another: total of all dice',
  score: ctx => (isFullHouse(ctx.dice) ? sum(ctx.dice) : 0),
};

export const yatzy: VariantDef = {
  ...base,
  id: 'yatzy',
  name: 'Yatzy',
  tagline: 'The Scandinavian table',
  description:
    'Fifteen boxes including One Pair and Two Pairs. A Yatzy is five of a kind (50). Kinds score only the matching dice, and the straights are exact runs.',
  family: 'regional',
  diceCount: 5,
  maxRolls: 3,
  categories: [
    ...upperCategories(),
    onePair,
    twoPairs,
    ofAKindOnly('threeKind', 'Three of a Kind', '3K', 3),
    ofAKindOnly('fourKind', 'Four of a Kind', '4K', 4),
    straightExact('smallStraight', 'Small Straight', 'Sm', [1, 2, 3, 4, 5], 15),
    straightExact('largeStraight', 'Large Straight', 'Lg', [2, 3, 4, 5, 6], 20),
    yatzyFullHouse,
    chance,
    fixedCategory(
      'yatzy',
      'Yatzy',
      'Y!',
      'Five of a kind (all five dice the same number): 50',
      50,
      ctx => highestOfAKind(ctx.dice, 5) > 0
    ),
  ],
  upperBonus: thresholdBonus(63, 50),
  upperBonusHint: '50 points at 63 or more',
  rules: [
    'Pairs and kinds score only the dice that form them (a pair of sixes is 12).',
    'Two Pairs needs two different values. Four of a kind is not two pairs.',
    'Small Straight is exactly 1-2-3-4-5 (15). Large Straight is 2-3-4-5-6 (20).',
    'Full House scores the total of all five dice. A Yatzy is five of a kind, all five dice the same number, and scores 50.',
    'Upper bonus: 50 points at 63 or more.',
  ],
};

/* ---------- Maxi Yatzy (6 dice) ---------- */

function pairsSum(ctx: ScoreContext, n: number): number {
  const p = pairValues(ctx.dice);
  const c = counts(ctx.dice);
  // Four of a kind may count as two pairs in Maxi Yatzy; six of a kind as three.
  const expanded: number[] = [];
  for (const v of p) {
    const k = Math.floor((c[v] ?? 0) / 2);
    for (let i = 0; i < k; i++) expanded.push(v);
  }
  if (expanded.length < n) return 0;
  return expanded.slice(0, n).reduce((a, v) => a + v * 2, 0);
}

function groups(ctx: ScoreContext, a: number, b: number): number {
  // Two disjoint groups of sizes a and b with different values.
  const c = counts(ctx.dice);
  for (let x = 6; x >= 1; x--) {
    if ((c[x] ?? 0) < a) continue;
    for (let y = 6; y >= 1; y--) {
      if (y === x || (c[y] ?? 0) < b) continue;
      return x * a + y * b;
    }
  }
  return 0;
}

export const maxiyatzy: VariantDef = {
  ...base,
  id: 'maxiyatzy',
  name: 'Maxi Yatzy',
  tagline: 'Six dice, twenty boxes',
  description:
    'Yatzy with a sixth die and a deeper card: Three Pairs, Five of a Kind, Full Straight, Villa and Tower. A Maxi Yatzy is six of a kind (100).',
  family: 'regional',
  diceCount: 6,
  maxRolls: 3,
  categories: [
    ...upperCategories(),
    onePair,
    { ...twoPairs, score: ctx => pairsSum(ctx, 2) },
    {
      id: 'threePairs',
      name: 'Three Pairs',
      short: '3P',
      section: 'lower',
      hint: 'Three pairs: total of all six dice',
      score: ctx => pairsSum(ctx, 3),
    },
    ofAKindOnly('threeKind', 'Three of a Kind', '3K', 3),
    ofAKindOnly('fourKind', 'Four of a Kind', '4K', 4),
    ofAKindOnly('fiveKind', 'Five of a Kind', '5K', 5),
    straightExact('smallStraight', 'Small Straight', 'Sm', [1, 2, 3, 4, 5], 15),
    straightExact('largeStraight', 'Large Straight', 'Lg', [2, 3, 4, 5, 6], 20),
    straightExact(
      'fullStraight',
      'Full Straight',
      'Full',
      [1, 2, 3, 4, 5, 6],
      21
    ),
    {
      id: 'fullHouse',
      name: 'Full House',
      short: 'FH',
      section: 'lower',
      hint: '3 of one and 2 of another: sum of those five dice',
      score: ctx => groups(ctx, 3, 2),
    },
    {
      id: 'villa',
      name: 'Villa',
      short: 'Vi',
      section: 'lower',
      hint: '3 of one and 3 of another: total of all six dice',
      score: ctx => groups(ctx, 3, 3),
    },
    {
      id: 'tower',
      name: 'Tower',
      short: 'Tw',
      section: 'lower',
      hint: '4 of one and 2 of another: total of all six dice',
      score: ctx => groups(ctx, 4, 2),
    },
    chance,
    fixedCategory(
      'maxiYatzy',
      'Maxi Yatzy',
      'MY!',
      'Six of a kind (all six dice the same number): 100',
      100,
      ctx => highestOfAKind(ctx.dice, 6) > 0
    ),
  ],
  upperBonus: thresholdBonus(84, 100),
  upperBonusHint: '100 points at 84 or more',
  rules: [
    'Six dice, three rolls, twenty boxes.',
    'Four of a kind may be split into two pairs; six of a kind into three pairs.',
    'Villa is two triples; Tower is four of one and two of another.',
    'Full Straight is 1 through 6 for 21. A Maxi Yatzy is six of a kind, all six dice the same number, and scores 100.',
    'Upper bonus: 100 points at 84 or more (four of each number).',
  ],
};

/* ---------- Yacht (1938) ---------- */

export const yacht: VariantDef = {
  ...base,
  id: 'yacht',
  name: 'Yacht',
  tagline: 'The 1938 original',
  description:
    'The English game Yahtzee was built on. A Yacht is five of a kind (50). No upper bonus, thirty-point straights, and Four of a Kind counts only the four dice.',
  family: 'regional',
  diceCount: 5,
  maxRolls: 3,
  categories: [
    ...upperCategories(),
    {
      id: 'fullHouse',
      name: 'Full House',
      short: 'FH',
      section: 'lower',
      hint: '3 of one and 2 of another: total of all dice',
      score: ctx => (isFullHouse(ctx.dice) ? sum(ctx.dice) : 0),
    },
    ofAKindOnly('fourKind', 'Four of a Kind', '4K', 4),
    straightExact(
      'littleStraight',
      'Little Straight',
      'Lt',
      [1, 2, 3, 4, 5],
      30
    ),
    straightExact('bigStraight', 'Big Straight', 'Big', [2, 3, 4, 5, 6], 30),
    { ...chance, id: 'choice', name: 'Choice', short: 'Cho' },
    fixedCategory(
      'yacht',
      'Yacht',
      'Y',
      'Five of a kind (all five dice the same number): 50',
      50,
      ctx => highestOfAKind(ctx.dice, 5) > 0
    ),
  ],
  upperBonus: noBonus,
  upperBonusHint: null,
  rules: [
    'Twelve boxes, no upper bonus, no bonus for extra Yachts.',
    'Little Straight is exactly 1-2-3-4-5; Big Straight is 2-3-4-5-6. Both score 30.',
    'Four of a Kind scores only the four matching dice. Full House scores all five.',
    'A Yacht is five of a kind, all five dice the same number, and scores 50.',
  ],
};

/* ---------- Generala (Latin America) ---------- */

function servedBonus(
  ctx: ScoreContext,
  points: number,
  served: number
): number {
  return ctx.rollsUsed === 1 ? served : points;
}

const GENERALA_STRAIGHTS = [
  [1, 2, 3, 4, 5],
  [2, 3, 4, 5, 6],
  [1, 3, 4, 5, 6],
];

export const generala: VariantDef = {
  ...base,
  id: 'generala',
  name: 'Generala',
  tagline: 'Serve it and win',
  description:
    'A Generala is five of a kind: all five dice showing the same number. Eleven boxes, no upper bonus. A combination is "served" when it shows on the first throw of a turn, before any re-roll: served Straights, Full Houses and Four of a Kinds score 5 extra, and a served Generala wins the game on the spot.',
  family: 'regional',
  diceCount: 5,
  maxRolls: 3,
  categories: [
    ...upperCategories(),
    {
      id: 'straight',
      name: 'Straight',
      short: 'Str',
      section: 'lower',
      hint: '1-2-3-4-5, 2-3-4-5-6, or 3-4-5-6 plus an ace: 20 (25 if served on the first throw)',
      score: ctx =>
        GENERALA_STRAIGHTS.some(s => containsAll(ctx.dice, s))
          ? servedBonus(ctx, 20, 25)
          : 0,
    },
    {
      id: 'fullHouse',
      name: 'Full House',
      short: 'FH',
      section: 'lower',
      hint: 'Three of one number and two of another: 30 (35 if served on the first throw)',
      score: ctx => (isFullHouse(ctx.dice) ? servedBonus(ctx, 30, 35) : 0),
    },
    {
      id: 'fourKind',
      name: 'Four of a Kind',
      short: '4K',
      section: 'lower',
      hint: 'Four of the same number: 40 (45 if served on the first throw)',
      score: ctx =>
        highestOfAKind(ctx.dice, 4) > 0 ? servedBonus(ctx, 40, 45) : 0,
    },
    {
      id: 'generala',
      name: 'Generala',
      short: 'Gen',
      section: 'lower',
      hint: 'Five of the same number: 50. Served on the first throw, it wins the game outright',
      score: ctx => (highestOfAKind(ctx.dice, 5) > 0 ? 50 : 0),
    },
    {
      id: 'doubleGenerala',
      name: 'Double Generala',
      short: 'DG',
      section: 'lower',
      hint: 'A second five of a kind, only after the Generala box already holds 50: 100',
      score: ctx =>
        highestOfAKind(ctx.dice, 5) > 0 && (ctx.column.boxes.generala ?? 0) > 0
          ? 100
          : 0,
    },
  ],
  upperBonus: noBonus,
  upperBonusHint: null,
  servedInstantWin: 'generala',
  rules: [
    'A Generala is five of a kind: all five dice showing the same number. It scores 50.',
    'Roll up to three times a turn. Ones through Sixes score the sum of that number; there is no upper bonus.',
    '"Served" means the combination shows on the first throw of your turn, before you re-roll anything. The scorecard marks served hands.',
    'Straight: 1-2-3-4-5, 2-3-4-5-6, or 3-4-5-6 with an ace filling in. Scores 20, or 25 served.',
    'Full House: three of one number and two of another. Scores 30, or 35 served.',
    'Four of a Kind: four of the same number. Scores 40, or 45 served.',
    'Generala: five of the same number. Scores 50. A served Generala ends the game immediately and you win.',
    'Double Generala: a second five of a kind, worth 100, but only once the Generala box already holds 50. Before that it is a scratch.',
    'No box fits? Scratch any open box for zero.',
  ],
};

/* ---------- Kismet (colored dice) ---------- */

export const KISMET_COLORS: Record<number, 'black' | 'red' | 'green'> = {
  1: 'black',
  6: 'black',
  2: 'red',
  5: 'red',
  3: 'green',
  4: 'green',
};

function colorOf(v: number) {
  return KISMET_COLORS[v];
}

function isFlush(dice: number[]): boolean {
  const c = colorOf(dice[0] ?? 1);
  return dice.every(d => colorOf(d) === c);
}

function twoPairSameColor(dice: number[]): boolean {
  const p = pairValues(dice);
  if (p.length < 2) return false;
  return colorOf(p[0]!) === colorOf(p[1]!);
}

function fullHouseSameColor(dice: number[]): boolean {
  if (!isFullHouse(dice)) return false;
  return isFlush(dice);
}

export const kismet: VariantDef = {
  ...base,
  id: 'kismet',
  name: 'Kismet',
  tagline: 'Color changes everything',
  description:
    'Pips are colored: 1 and 6 black, 2 and 5 red, 3 and 4 green. Matching colors unlock flushes and a tiered upper bonus. A Kismet is five of a kind.',
  family: 'official',
  diceCount: 5,
  maxRolls: 3,
  categories: [
    ...upperCategories(),
    {
      id: 'twoPairSameColor',
      name: 'Two Pair Same Color',
      short: '2P=',
      section: 'lower',
      hint: 'Two pairs whose pips are the same color (e.g. 2s and 5s, both red): total of all dice',
      score: ctx => (twoPairSameColor(ctx.dice) ? sum(ctx.dice) : 0),
    },
    ofAKindAll('threeKind', 'Three of a Kind', '3K', 3),
    fixedCategory(
      'straight',
      'Straight',
      'Str',
      'Five in a row: 30',
      30,
      ctx =>
        containsAll(ctx.dice, [1, 2, 3, 4, 5]) ||
        containsAll(ctx.dice, [2, 3, 4, 5, 6])
    ),
    fixedCategory(
      'flush',
      'Flush',
      'Fl',
      'All five dice showing pips of one color: 35',
      35,
      ctx => isFlush(ctx.dice)
    ),
    {
      id: 'fullHouse',
      name: 'Full House',
      short: 'FH',
      section: 'lower',
      hint: 'Three of one number and two of another: total of all dice plus 15',
      score: ctx => (isFullHouse(ctx.dice) ? sum(ctx.dice) + 15 : 0),
    },
    {
      id: 'fullHouseSameColor',
      name: 'Full House Same Color',
      short: 'FH=',
      section: 'lower',
      hint: 'Three of one number and two of another, all pips one color: total plus 20',
      score: ctx => (fullHouseSameColor(ctx.dice) ? sum(ctx.dice) + 20 : 0),
    },
    {
      id: 'fourKind',
      name: 'Four of a Kind',
      short: '4K',
      section: 'lower',
      hint: 'Four of the same number: total of all dice plus 25',
      score: ctx => (highestOfAKind(ctx.dice, 4) > 0 ? sum(ctx.dice) + 25 : 0),
    },
    {
      id: 'yarborough',
      name: 'Yarborough',
      short: 'Yb',
      section: 'lower',
      hint: 'Anything goes: total of all dice',
      score: ctx => sum(ctx.dice),
    },
    {
      id: 'kismet',
      name: 'Kismet',
      short: 'K!',
      section: 'lower',
      hint: 'Five of a kind (all five dice the same number): total of all dice plus 50',
      score: ctx => (highestOfAKind(ctx.dice, 5) > 0 ? sum(ctx.dice) + 50 : 0),
    },
  ],
  upperBonus: upper =>
    upper >= 78 ? 75 : upper >= 71 ? 55 : upper >= 63 ? 35 : 0,
  upperBonusHint: '35 at 63-70, 55 at 71-77, 75 at 78+',
  pipColors: KISMET_COLORS,
  rules: [
    'Pip colors: 1 and 6 are black, 2 and 5 are red, 3 and 4 are green.',
    'Two Pair Same Color, Flush (35) and Full House Same Color need matching pip colors.',
    'Full House adds 15, Four of a Kind adds 25 and Kismet adds 50 to the dice total.',
    'Yarborough is the anything-goes box, scoring the total of the dice. A Kismet is five of a kind: total of the dice plus 50.',
    'Upper bonus tiers: 35 at 63-70, 55 at 71-77, 75 at 78 or more.',
  ],
};
